// Direct-load pair-channel bootstrap. On a direct pair URL the exact
// subscribe set usePairFeed will need is derivable before React mounts: the
// global 'trades' channel immediately (filtered to the route's master), and
// the exact pair:{pool}:trades / pair:{pool}:activity channels as soon as the
// module-init /resolve prefetch answers. Subscribing here means the server
// ACK is already recorded when usePairFeed's seed barrier asks for it — the
// subscribe-before-seed barrier is unchanged, it just resolves without
// waiting for a mount-time round-trip.
//
// Events that arrive before the hook's own subscriptions install are
// buffered per channel and flushed through the hook handler at claim time,
// in arrival order, synchronously inside useWsSubscription's effect — so a
// buffered event takes the exact same barrier path (preRouteTradeBuffer /
// seedBarrierRef) as one arriving live at mount, and no live event can
// overtake a buffered one. The base pair:{pool} channel is deliberately NOT
// bootstrapped: it is not part of the seed barrier and has two hook
// subscribers (liquidity + token.profile), which a single claim flush could
// not serve without dropping one consumer's events.
//
// Ownership: a channel is claimed exactly once (claim releases the bootstrap
// subscription; the hook's own subscription keeps the server-side channel
// open). A channel never claimed — navigation away before mount, a chunk
// that fails to load, a resolve whose pool the hook revalidates away from —
// is released after CLAIM_TTL_MS so the socket never leaks subscriptions.

import type { Channel, WsClient, WsMessage } from '../ws/WsClient'
import { peekPairResolvePrefetch } from './pairResolvePrefetch'
import type { PairRouteRef } from './pairRoutes'

const CLAIM_TTL_MS = 30_000
// Bound on buffered pre-mount events per channel. Overflow drops the OLDEST
// events; this can only lose what the pre-#this-PR client never saw at all
// (it was not subscribed before mount), and the seed barrier reconciles.
const MAX_BUFFERED_MESSAGES = 512

interface BufferedChannel {
  messages: WsMessage[]
  unsubscribe: () => void
  expiry: ReturnType<typeof setTimeout>
}

const channels = new Map<string, BufferedChannel>()

type Subscribable = Pick<WsClient, 'subscribe'>

function releaseChannel(channel: string): void {
  const entry = channels.get(channel)
  if (!entry) return
  channels.delete(channel)
  clearTimeout(entry.expiry)
  entry.unsubscribe()
}

function subscribeBuffered(
  client: Subscribable,
  channel: Channel,
  keep: (msg: WsMessage) => boolean
): void {
  if (channels.has(channel)) return
  const entry: BufferedChannel = {
    messages: [],
    unsubscribe: () => undefined,
    expiry: setTimeout(() => releaseChannel(channel), CLAIM_TTL_MS),
  }
  entry.unsubscribe = client.subscribe(channel, (msg) => {
    if (!keep(msg)) return
    if (entry.messages.length >= MAX_BUFFERED_MESSAGES) entry.messages.shift()
    entry.messages.push(msg)
  })
  channels.set(channel, entry)
}

export function startPairWsBootstrap(client: Subscribable, ref: PairRouteRef): void {
  const addr = ref.addr
  if (!addr || addr === 'native') return
  // The app-wide trade stream, pre-filtered to this route's master exactly
  // the way usePairFeed's pre-resolve handler filters it (pool_addr equality
  // with the not-yet-resolved pool is impossible here by construction).
  subscribeBuffered(client, 'trades', (msg) => {
    if (msg.event !== 'trade') return false
    const trade = msg.data as { base_token?: unknown; pool_addr?: unknown } | null
    return trade?.base_token === addr || trade?.pool_addr === addr
  })
  // The exact pool channels need the resolved pool. Chain to the module-init
  // resolve prefetch (peek, not consume — usePairFeed remains the consumer);
  // a failed resolve simply never bootstraps and the hook subscribes live.
  const resolved = peekPairResolvePrefetch(addr, ref.mode)
  if (!resolved) return
  void resolved.then(
    (route) => {
      if (!route?.pool_addr) return
      subscribeBuffered(client, `pair:${route.pool_addr}:trades` as Channel, () => true)
      subscribeBuffered(client, `pair:${route.pool_addr}:activity` as Channel, () => true)
    },
    () => undefined
  )
}

/** Hands a bootstrapped channel to its real consumer: releases the bootstrap
    subscription and returns the buffered messages in arrival order. Returns
    undefined when the channel was never bootstrapped (or already claimed) —
    the overwhelmingly common case for every non-pair channel. */
export function claimPairWsBootstrap(channel: Channel): WsMessage[] | undefined {
  const entry = channels.get(channel)
  if (!entry) return undefined
  releaseChannel(channel)
  return entry.messages
}

export function clearPairWsBootstrapForTest(): void {
  for (const channel of [...channels.keys()]) releaseChannel(channel)
}
